// /onboarding — guided flow for a business owner to appear on OnHere.
//
// Steps:
//   1. Basics — name, category, description
//   2. Location — address, phone, website
//   3. Confirm + create
//
// Creates a Business Group (founder = current user, admin role) and a
// Place row linked to it. If ?partner=<id> is in the URL, attribute the
// new Group to that partner's book automatically.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';

type Step = 'basics' | 'location' | 'review' | 'done';

interface Community { id: string; slug: string; name: string }
interface Partner   { id: string; display_name: string; contact_email: string; commission_percent: number }

const CATEGORIES = ['cafe', 'restaurant', 'retail', 'salon', 'service', 'grocery', 'community', 'other'];

export function BusinessOnboarding() {
    const api = useApi();
    const nav = useNavigate();
    const { authed, login } = useAuth();
    const [params] = useSearchParams();
    const partnerParam = params.get('partner');

    const [step, setStep] = useState<Step>('basics');
    // Basics
    const [name,        setName]        = useState('');
    const [slug,        setSlug]        = useState('');
    const [category,    setCategory]    = useState('cafe');
    const [description, setDescription] = useState('');
    // Location
    const [address, setAddress] = useState('');
    const [phone,   setPhone]   = useState('');
    const [website, setWebsite] = useState('');
    const [hoursMon, setHoursMon] = useState('');
    // Community + partner attribution
    const [communityID, setCommunityID] = useState('');
    const [communities, setCommunities] = useState<Community[]>([]);
    const [partner,     setPartner]     = useState<Partner | null>(null);
    // Submit state
    const [status,    setStatus]    = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);
    const [createdSlug, setCreatedSlug] = useState<string>('');

    // Load communities + partner details.
    useEffect(() => {
        if (!authed) return;
        api.get<{ communities: Community[] }>('/api/communities').then(r => {
            setCommunities(r.communities ?? []);
            if (r.communities?.length) setCommunityID(r.communities[0].id);
        });
        if (partnerParam) {
            api.get<Partner>('/api/partners/' + partnerParam)
                .then(setPartner).catch(() => setPartner(null));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed]);

    // Auto-suggest slug from name.
    useEffect(() => {
        if (!slug && name) setSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const submit = useCallback(async () => {
        setStatus({ tone: 'busy', msg: 'Creating your business…' });
        try {
            // 1. Create the Business Group.
            const group = await api.post<{ id: string; slug: string }>('/api/groups', {
                name,
                slug,
                type: 'business',
                description: description || null,
                tag_ids: [],
            });
            // 2. Create the Place linked to the Group.
            const hours: Record<string, string> = {};
            if (hoursMon) hours.mon = hoursMon;
            await api.post('/api/places', {
                slug,
                name,
                category,
                address:      address || null,
                phone:        phone   || null,
                website:      website || null,
                description:  description || null,
                hours,
                owner_group_id: group.id,
                tag_ids: [],
            }).catch(() => { /* place slug collision — carry on, Group was created */ });
            // 3. If arrived via a partner link, attribute the group.
            if (partnerParam) {
                await api.post('/api/partners/' + partnerParam + '/attribute', {
                    group_id: group.id,
                }).catch(() => { /* attribution failure is silent — group is still theirs */ });
            }
            setCreatedSlug(group.slug);
            setStatus({ tone: 'ok', msg: '✓ Your business is live' });
            setStep('done');
        } catch (err) {
            setStatus({ tone: 'err', msg: (err as Error).message });
        }
    }, [api, name, slug, category, description, address, phone, website, hoursMon, partnerParam]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to onboard your business.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main>
            <div className="onboarding-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Get on OnHere</h1>
                    <div className="issue">Onboard your business — takes 2 minutes</div>
                    <div className="rule-bottom" />
                </div>

                {partner && (
                    <div className="onboarding-partner">
                        <strong>You're being onboarded by {partner.display_name}</strong>
                        — your business will be attributed to them ({partner.commission_percent}% commission).
                        Get in touch at <a href={'mailto:' + partner.contact_email}>{partner.contact_email}</a> if
                        that's news to you.
                    </div>
                )}

                <StepPips step={step} />

                {step === 'basics' && (
                    <section className="onboarding-step">
                        <h2>1 · The basics</h2>
                        <label>
                            <span>Business name</span>
                            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                                   placeholder='e.g. "Kloofnek Cafe"' />
                        </label>
                        <label>
                            <span>URL slug</span>
                            <input type="text" required pattern="[a-z0-9\-]+" value={slug} onChange={(e) => setSlug(e.target.value)}
                                   placeholder="kloofnek-cafe" />
                            <em>lowercase, dashes only — your OnHere URL will be /places/{slug || 'your-slug'}</em>
                        </label>
                        <label>
                            <span>Category</span>
                            <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Short description</span>
                            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                                      placeholder="Family-run coffee shop on Kloofnek Rd. Great for laptop work + Sunday brunch." />
                        </label>
                        <div className="actions">
                            <button type="button" className="primary" disabled={!name || !slug}
                                    onClick={() => setStep('location')}>Next: location →</button>
                        </div>
                    </section>
                )}

                {step === 'location' && (
                    <section className="onboarding-step">
                        <h2>2 · Where + when</h2>
                        <label>
                            <span>Address</span>
                            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
                                   placeholder="12 Kloofnek Rd, Vredehoek" />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                   placeholder="+27 21 …" />
                        </label>
                        <label>
                            <span>Website</span>
                            <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)}
                                   placeholder="https://…" />
                        </label>
                        <label>
                            <span>Weekday hours (Mon–Fri)</span>
                            <input type="text" value={hoursMon} onChange={(e) => setHoursMon(e.target.value)}
                                   placeholder='e.g. "07-16"' />
                            <em>Free-form — richer hours editor lands soon.</em>
                        </label>
                        {communities.length > 1 && (
                            <label>
                                <span>Home community</span>
                                <select value={communityID} onChange={(e) => setCommunityID(e.target.value)}>
                                    {communities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                        )}
                        <div className="actions">
                            <button type="button" className="plain" onClick={() => setStep('basics')}>← Back</button>
                            <button type="button" className="primary" onClick={() => setStep('review')}>Next: review →</button>
                        </div>
                    </section>
                )}

                {step === 'review' && (
                    <section className="onboarding-step">
                        <h2>3 · Review</h2>
                        <dl className="kv">
                            <dt>Name</dt><dd>{name}</dd>
                            <dt>Category</dt><dd>{category}</dd>
                            <dt>URL</dt><dd>/places/{slug}</dd>
                            {description && <><dt>Description</dt><dd>{description}</dd></>}
                            {address && <><dt>Address</dt><dd>{address}</dd></>}
                            {phone   && <><dt>Phone</dt><dd>{phone}</dd></>}
                            {website && <><dt>Website</dt><dd>{website}</dd></>}
                            {hoursMon && <><dt>Weekday hours</dt><dd>{hoursMon}</dd></>}
                            {partner && <><dt>Attributed to</dt><dd>{partner.display_name}</dd></>}
                        </dl>
                        <div className="actions">
                            <button type="button" className="plain"   onClick={() => setStep('location')}>← Back</button>
                            <button type="button" className="primary" onClick={submit}>Create my business →</button>
                            {status && <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>}
                        </div>
                    </section>
                )}

                {step === 'done' && (
                    <section className="onboarding-step onboarding-done">
                        <h2>✓ You're on OnHere</h2>
                        <p>
                            Your business is live. You're the admin — post updates, offers,
                            events straight from the "+" menu, and manage members from your
                            group dashboard.
                        </p>
                        <div className="actions">
                            <button type="button" className="primary" onClick={() => nav('/groups/' + createdSlug + '/dashboard')}>
                                Go to my dashboard →
                            </button>
                            <button type="button" className="plain" onClick={() => nav('/')}>
                                Back to the paper
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </main>
    );
}

function StepPips({ step }: { step: Step }) {
    const steps: Array<[Step, string]> = [
        ['basics',   'Basics'],
        ['location', 'Location'],
        ['review',   'Review'],
        ['done',     'Done'],
    ];
    const idx = steps.findIndex(([s]) => s === step);
    return (
        <div className="onboarding-pips">
            {steps.map(([s, label], i) => (
                <div key={s} className={'onboarding-pip' + (i <= idx ? ' is-done' : '')}>
                    <span className="pip-n">{i + 1}</span>
                    <span className="pip-label">{label}</span>
                </div>
            ))}
        </div>
    );
}

